<template>
  <div v-if="abierto">
    <div class="modal fade show" style="display: block" tabindex="-1" role="dialog" aria-modal="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <form @submit.prevent="confirmar">
            <div class="modal-header">
              <h5 class="modal-title">Apertura de Caja</h5>
              <button type="button" class="btn-close" @click="cerrar" />
            </div>

            <div class="modal-body">
              <div v-if="error" class="alert alert-danger py-2 px-3" style="font-size: 13.5px" role="alert"
                   data-test="error-apertura">
                {{ error }}
              </div>

              <div class="mb-3">
                <label class="form-label" for="cashboxOpeningSucursal">Sucursal</label>
                <select id="cashboxOpeningSucursal" class="form-select" v-model.number="formulario.sucursalId"
                        :disabled="cargando" @change="cambiarSucursal" required>
                  <option v-for="sucursal in contexto.sucursales" :key="'sucursal-' + sucursal.sucursal_id" :value="sucursal.sucursal_id">
                    {{ sucursal.nombre_establecimiento }} ({{ sucursal.sucursal_id }})
                  </option>
                </select>
              </div>

              <div class="mb-3">
                <label class="form-label" for="cashboxOpeningCaja">Caja</label>
                <select id="cashboxOpeningCaja" class="form-select" v-model.number="formulario.cajaId"
                        :disabled="cargando" required>
                  <option v-for="caja in contexto.cajas" :key="'caja-' + caja.caja_id" :value="caja.caja_id">
                    {{ etiquetaDeCaja(caja) }}
                  </option>
                </select>
                <small v-if="formulario.sucursalId && !cargando && !contexto.cajas.length" class="text-danger">
                  No hay cajas activas configuradas para esta sucursal habilitada.
                </small>
              </div>

              <div class="mb-3">
                <label class="form-label" for="cashboxOpeningDate">Fecha y hora</label>
                <input type="datetime-local" id="cashboxOpeningDate" class="form-control"
                       v-model="formulario.fechaApertura" required />
              </div>

              <div class="mb-3">
                <label class="form-label" for="cashboxOpeningAmount">Monto inicial</label>
                <MoneyInput input-id="cashboxOpeningAmount" v-model="formulario.montoInicial"
                            placeholder="0" emit-zero-when-empty />
              </div>

              <div class="mb-3">
                <label class="form-label" for="cashboxOpeningObservation">Observación (opcional)</label>
                <input type="text" id="cashboxOpeningObservation" class="form-control"
                       v-model="formulario.observacion" />
              </div>
            </div>

            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="cerrar">Cancelar</button>
              <button type="submit" class="btn btn-primary" :disabled="!puedeAbrir" data-test="btn-confirmar-apertura">
                <span v-if="guardando" class="spinner-border spinner-border-sm me-1"></span>
                {{ guardando ? 'Abriendo…' : 'Abrir Caja' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show"></div>
  </div>
</template>

<script setup>
// Apertura de caja, en una sola pieza reusable.
//
// La apertura vivía dentro de `CajaView.vue` y solo se podía hacer yendo a
// /caja. Eso obliga al cajero que choca con `caja_no_abierta` en mitad de un
// cobro a abandonar la pantalla —y con ella las cuotas que ya había marcado y
// los montos que ya había tipeado— para volver a empezar. El modal se extrajo
// para que cualquier pantalla que dependa de la caja pueda ofrecer la salida
// sin navegar a ningún lado. La lógica es la de `CajaView`, no una nueva:
// mismo contexto de apertura, mismo POST, mismo refresco del store.

import { reactive, ref, computed, watch } from 'vue'
import MoneyInput from '@/components/MoneyInput.vue'
import apiService from '@/services/apiService'
import operationalConfigService from '@/services/operationalConfigService'
import operationalContextService from '@/services/operationalContextService'
import { useCashboxStore } from '@/stores/cashbox'

/* global defineProps, defineEmits */
const props = defineProps({
  abierto: { type: Boolean, default: false }
})
const emit = defineEmits(['update:abierto', 'abierta'])

const cashboxStore = useCashboxStore()

const cargando = ref(false)
const guardando = ref(false)
const error = ref('')

const contexto = reactive({ sucursales: [], cajas: [] })
const formulario = reactive({
  sucursalId: null,
  cajaId: null,
  fechaApertura: '',
  montoInicial: null,
  moneda: 'PYG',
  observacion: ''
})

const puedeAbrir = computed(() => Boolean(
  formulario.sucursalId && formulario.cajaId && formulario.fechaApertura
  && formulario.montoInicial !== null && !guardando.value && !cargando.value
))

// El formulario se arma al ABRIR y no al montar: si no, la fecha de apertura
// sería la de cuando se cargó la pantalla y no la de cuando se abre la caja.
watch(() => props.abierto, (abierto) => { if (abierto) preparar() })

async function preparar () {
  error.value = ''
  formulario.fechaApertura = ahoraLocalISO()
  formulario.montoInicial = null
  formulario.observacion = ''
  formulario.sucursalId = operationalConfigService.getCurrentSucursalId()
  formulario.cajaId = null

  cargando.value = true
  try {
    await cargarContexto(formulario.sucursalId)
  } catch (err) {
    error.value = err?.userMessage || 'No se pudo cargar el contexto de apertura de caja.'
  } finally {
    cargando.value = false
  }
}

async function cargarContexto (sucursalId = null) {
  const datos = await operationalContextService.getCashboxOpeningContext(
    sucursalId ? { sucursal_id: sucursalId } : {}
  )
  contexto.sucursales = Array.isArray(datos?.sucursales) ? datos.sucursales : []
  contexto.cajas = Array.isArray(datos?.cajas) ? datos.cajas : []
  formulario.sucursalId = Number(datos?.sucursal_id || contexto.sucursales[0]?.sucursal_id || null)
  if (!contexto.cajas.some((item) => Number(item.caja_id) === Number(formulario.cajaId))) {
    formulario.cajaId = Number(contexto.cajas[0]?.caja_id || null)
  }
  if (formulario.sucursalId) {
    localStorage.setItem('sucursal_id', String(formulario.sucursalId))
  }
}

async function cambiarSucursal () {
  cargando.value = true
  error.value = ''
  try {
    await cargarContexto(formulario.sucursalId)
  } catch (err) {
    error.value = err?.userMessage || 'No se pudieron cargar las cajas de esta sucursal.'
  } finally {
    cargando.value = false
  }
}

function etiquetaDeCaja (caja) {
  const punto = caja?.codigo_punto_expedicion ? ` - Punto ${caja.codigo_punto_expedicion}` : ''
  return `${caja?.codigo_caja || 'Caja'} - ${caja?.nombre || ''}${punto}`
}

function ahoraLocalISO () {
  const ahora = new Date()
  const desfase = ahora.getTimezoneOffset() * 60000
  return new Date(ahora - desfase).toISOString().slice(0, 16) // YYYY-MM-DDTHH:mm
}

function cerrar () {
  emit('update:abierto', false)
}

async function confirmar () {
  guardando.value = true
  error.value = ''
  try {
    await apiService.post('/api/cashbox/open', {
      fecha_apertura: formulario.fechaApertura,
      monto_inicial: formulario.montoInicial,
      moneda: formulario.moneda,
      observacion: formulario.observacion,
      sucursal_id: formulario.sucursalId,
      caja_id: formulario.cajaId
    })
    await cashboxStore.fetchCurrentOpen()
    emit('update:abierto', false)
    emit('abierta')
  } catch (err) {
    error.value = err?.userMessage || 'No se pudo abrir la caja.'
  } finally {
    guardando.value = false
  }
}
</script>
